<script lang="ts">
  import { onMount } from 'svelte';
  import IntroPartial from '$components/common/partials/IntroPartial.svelte';
  import BoxFixesPartial from '$components/common/partials/BoxFixesPartial.svelte';
  import RichTextFixesPartial from '$components/common/partials/RichTextFixesPartial.svelte';
  import LinkFixesPartial from '$components/common/partials/LinkFixesPartial.svelte';
  import VideoFixesPartial from '$components/common/partials/VideoFixesPartial.svelte';
  import { activeVerticalTabName } from '../HomePageTabSection/store';

  // swap animations for the "build custom" section on vertical tab changes
  const handleBuildCustomVerticalTabChange = (vertical: string) => {
    const hiddenClass = 'sq-custom-hidden';
    const animationBaseSelector = '.sq-custom-build-animation';
    const allAnimations = document.querySelectorAll(animationBaseSelector);
    const defaultAnimation = document.querySelector(`${animationBaseSelector}-default`);
    const currentAnimation = document.querySelector(`${animationBaseSelector}-${vertical}`);
    // allow scroll animation to happen
    setTimeout(() => {
      // hide all animations by default
      allAnimations.forEach((animation) => {
        animation.classList.add(hiddenClass);
      });
      if (currentAnimation) {
        // if an animation matches the selected vertical, show it
        currentAnimation.classList.remove(hiddenClass);
      } else {
        // if no animation matches the vertical, show the default
        if (defaultAnimation) {
          defaultAnimation.classList.remove(hiddenClass);
        }
      }
    }, 1000);
  };

  // allows standard components respond to vertical tab changes
  const verticalSetup = () => {
    activeVerticalTabName.subscribe((vertical) => {
      if (vertical) {
        handleBuildCustomVerticalTabChange(vertical);
      }
    });
  };

  // hides the initial form header text after the user submits the email
  // so only the follow up text is shown
  const leadFormSetup = () => {
    const form = document.querySelector('.sq-custom-two-col-lead-form');
    form?.addEventListener('submit', () => {
      const initialFormHeader = form.querySelector('.sq-custom-initial-form-header');
      const initialFormSubheader = form.querySelector('.sq-custom-initial-form-subheader');
      (initialFormHeader as HTMLElement).classList.add('hidden-until-large');
      (initialFormSubheader as HTMLElement).classList.add('hidden');
    });
  };

  onMount(() => {
    // handle vertical tab changes
    verticalSetup();
    // custom lead form handling
    leadFormSetup();
  });
</script>

<IntroPartial />
<BoxFixesPartial />
<RichTextFixesPartial />
<LinkFixesPartial />
<VideoFixesPartial />

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/core/config/_media_queries' as media;

  // animation for 'build custom experiences' gradient
  @keyframes -global-blueCustomGradient {
    0% {
      transform: rotate(0deg) scale(1, 1);
    }
    25% {
      transform: rotate(90deg) scale(1.4, 1);
    }
    50% {
      transform: rotate(180deg) scale(1, 1);
    }
    75% {
      transform: rotate(270deg) scale(1, 1.4);
    }
    100% {
      transform: rotate(360deg) scale(1, 1);
    }
  }

  :global {
    .BasePage {
      --font-size-body: 1.6rem;
      --line-height-body: 2.4rem;
    }

    // Overriding existing contentful h1 class since changing
    // this in the prod entry prior will cause regressions
    .BasePage h2 .h1 {
      --font-size-h1: var(--font-size-h2);
      --line-height-h1: var(--line-height-h2);
    }

    .BasePage .LinkComponent--button-alt {
      line-height: 2.2rem !important;
    }

    .BasePage .LinkComponent--button {
      line-height: 2.2rem !important;
    }

    #footer {
      border-top: 1px solid var(--color-gray-55);
    }

    .hidden-until-large {
      @include media.until('large') {
        display: none;
      }
    }

    .PublicWebNav {
      .FlyoutFlattened {
        border-left-width: 0px;
        border-right-width: 0px;
      }

      .DesktopNav,
      .MobileNav {
        border-color: transparent !important;
      }
    }

    .sq-custom-dark-gray-background {
      background-color: #1a1a1a;
    }

    .sq-custom-border-gray-55 {
      border-color: var(--color-gray-55) !important;
    }

    // fixes image width issue for the support section icons
    .sq-custom-image-width-medium {
      width: var(--space-60) !important;
    }

    // custom hidden for animation swap
    .sq-custom-hidden {
      display: none !important;
    }

    .sq-custom-support-tiles {
      --min-card-height: 290px;
      // align content to bottom
      > a,
      > div {
        min-height: var(--min-card-height);
        display: flex !important;
        flex-direction: column;
      }
      // set font sizes for support tiles
      a {
        font-size: var(--font-size-body-large) !important;
        line-height: var(--line-height-body-large) !important;
      }

      @include media.at('medium') {
        --min-card-height: 420px;
      }
    }

    // generic logo module
    .sq-custom-fixed-height-logo-module {
      --fixed-height: 32px;

      .AproposImageComponent img {
        width: auto;
        height: var(--fixed-height);
      }
    }

    // the kiosk banner module on the AU page only
    .sq-custom-kiosk-promo-banner-module {
      > .FlexLayoutSection {
        background: linear-gradient(268deg, #fbffe8 7.46%, #cae9f5 93.74%);
      }
    }

    // the canstar module on the AU/UK pages
    .sq-custom-fixed-width-logo-module {
      .AproposImageComponent {
        width: var(--space-50);
      }

      @include media.at('medium') {
        display: flex;

        .AproposImageComponent {
          width: 100%;
          max-width: var(--space-80);
          flex: 1 0 auto;
          margin-right: var(--space-20);
        }
      }
    }

    // the trustpilot module on the AU/UK pages
    .sq-custom-trustpilot-module {
      .TrustpilotRating a {
        display: block !important;

        .stars {
          margin-bottom: var(--space-10);
        }
      }

      @include media.at('small') {
        .TrustpilotRating a {
          display: inline-flex !important;

          .stars {
            margin-bottom: 0;
          }
        }
      }
    }

    // add gradient to build custom experiences section
    .sq-custom-build-custom {
      overflow: visible !important;
      contain: layout;

      &::before {
        content: ' ';
        display: block;
        position: absolute;
        left: 100%;
        top: -30vw;
        margin-left: -40vw;
        width: 80vw;
        height: 80vw;
        transform: rotate(0deg) scale(1, 1);
        background: rgb(4, 70, 163);
        background: radial-gradient(
          circle,
          rgba(4, 70, 163, 1) 0%,
          rgba(4, 70, 163, 0.9080225840336135) 11%,
          rgba(4, 70, 163, 0.7679665616246498) 25%,
          rgba(0, 0, 0, 0) 55%
        );
        animation: blueCustomGradient 12s ease-in-out infinite;
      }

      @include media.at('medium') {
        &::before {
          width: 60vw;
          height: 60vw;
        }

        .AnimationComponent {
          // shrink animation to fit better vertically on medium+ size screens
          svg {
            max-height: max(80vh, 700px);
          }
        }
      }

      // keep the gradient at a fixed size and close to the content on very large screens
      @media screen and (min-width: 2000px) {
        &::before {
          left: 50%;
          width: 1300px;
          height: 1300px;
          margin-left: calc(var(--body-max-width) / 2);
          top: -700px;
        }
      }
    }

    // two column lead form with gradient
    .sq-custom-two-col-lead-form {
      .FlexLayoutSection {
        position: relative;
        z-index: 1;
      }

      .LeadFormSection .content.content-width-medium {
        width: 100%;

        // privacy and opt in text below email field
        form .rtfContainer p {
          color: white;
        }

        .VisibleField label {
          color: var(--color-gray-30) !important;
        }
      }

      .sq-custom-lead-form-gradient {
        position: absolute;
        left: 50%;
        bottom: 0;
        transform: translateX(-50%);
        width: 100vw;
        max-width: 1920px; // arbitrary, but matches max body width
      }
    }

    // animated flow diagram module
    .sq-custom-flow-diagram {
      position: relative;

      .AnimationComponent {
        position: relative;
        margin: calc(-1 * var(--space-30)) auto 0;
        width: 86%; // designed width
      }

      @include media.at('small') {
        .AnimationComponent {
          width: 50%;
        }
      }

      @include media.at('medium') {
        .AproposImageComponent {
          width: 84%; // designed width
        }

        .AnimationComponent {
          position: absolute;
          margin: 0;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 30%; // As per slack convo - https://square.slack.com/archives/C03SXJ2NRFF/p1701129342427379
        }
      }
    }
  }
</style>
